<script>
/**
 * ****************************************************************************
 * @description     :   Search Box Component for FAQ
 * @created at      :   2025/06/08
 * @created by      :   DaoNX
 * @package         :   Move
 * @copyright       :   Copyright (c) A.N.S Asia
 * @version         :   1.0.0
 * ****************************************************************************
 */

/**
 * Import template
 */
import template from './template.html';
import './style.scss';
import store from '@/store';
import SearchBoxStore from './store';
import { mapActions, mapState } from 'vuex';

/**
 * Define component for FAQ Search Box
 */
export default {
    name: 'SearchBox',
    template: template,
    props: {
        initialValue: {
            type: String,
            default: ''
        },
        placeholder: {
            type: String,
            default: 'キーワードで検索する'
        }
    },
    data() {
        return {
            internalSearchTerm: this.initialValue || '',
            showSuggestions: false,
            searchDebounceTimer: null
        };
    },
    computed: {
        ...mapState('SearchBoxStore', ['suggestions'])
    },
    beforeCreate() {
        // Register the store module if it's not already registered
        try {
            if (!store.hasModule('SearchBoxStore')) {
                store.registerModule('SearchBoxStore', SearchBoxStore);
            }
        } catch (error) {
            console.error('Error registering SearchBoxStore module:', error);
        }
    },
    created() {
        try {
            this.onInputChange();
        } catch (error) {
            console.error('Error during component initialization:', error);
        }
    },
    methods: {
        ...mapActions('SearchBoxStore', ['fetchSuggestions', 'updateKeywordCount']),
        handleSearch() {
            const currentValue = this.internalSearchTerm.trim();
            if (currentValue) {
                // Emit search event with the search term
                this.$emit('search', currentValue);

                // Don't clear input immediately - let parent component handle navigation
                // The input will be properly updated when the parent navigates to search page

                // Blur the input to hide suggestions
                this.blurInput();
            }
        },
        blurInput() {
            // Using the ref to access the input element
            if (this.$refs.searchInput) {
                // Blur the input to lose focus
                this.$refs.searchInput.blur();

                // Explicitly hide the suggestions
                this.showSuggestions = false;
            }
        },

        handleBlur() {
            setTimeout(() => {
                this.showSuggestions = false;
            }, 200);
        },
        handleInput(event) {
            this.internalSearchTerm = event.target.value || '';
            this.onInputChange();
        },
        onInputChange() {
            // Clear previous debounce timer
            if (this.searchDebounceTimer) {
                clearTimeout(this.searchDebounceTimer);
            }

            // Get current input value
            const keyword = this.internalSearchTerm.trim();

            if (!keyword) {
                try {
                    this.fetchSuggestions({ keyword: '' });
                } catch (error) {
                    console.error('Error clearing suggestions:', error);
                }
                return;
            }

            // Debounce search to avoid too many API calls
            this.searchDebounceTimer = setTimeout(() => {
                try {
                    this.fetchSuggestions({ keyword });
                } catch (error) {
                    console.error('Error fetching suggestions:', error);
                }
            }, 300);
        },
        selectSuggestion(suggestion) {
            // Update keyword count if keywordId exists (silent operation)
            if (suggestion.keywordId) {
                this.updateKeywordCount({ keywordId: suggestion.keywordId })
                    .then((result) => {
                        if (result && result.Code !== 200) {
                            console.warn('Keyword count update failed silently:', result);
                        }
                    })
                    .catch((error) => {
                        // Silent fail - don't block user interaction
                        console.warn('Failed to update keyword count (silent):', error);
                    });
            }

            // Check if suggestion has URL - proceed regardless of keyword count update result
            if (suggestion.url && suggestion.url.trim()) {
                // Navigate using Vue router
                this.$router.push(suggestion.url);
            }

            this.showSuggestions = false;

            // Blur the input when a suggestion is selected
            this.blurInput();
        }
    },
    beforeUnmount() {
        // Clear debounce timer on component destroy
        if (this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer);
        }
    }
};
</script>
